import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Toaster } from "sonner";
import { ArrowLeft, Phone, Mail, MapPin, Filter } from "lucide-react";

const STATUSES = ["new", "contacted", "report_pending", "report_received", "eligible_for_evaluation", "consult_scheduled", "funding_required", "surgery_planned", "surgery_done", "switch_on_done", "rehab_ongoing", "closed"];

export default function AdminLeads() {
  const nav = useNavigate();
  const [leads, setLeads] = useState([]);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  const [selected, setSelected] = useState(null);
  const [edit, setEdit] = useState({ status: "", notes: "", assigned_counsellor: "" });

  const fetchLeads = async () => {
    try {
      const params = {};
      if (filterStatus) params.status_filter = filterStatus;
      if (filterType) params.lead_type = filterType;
      const { data } = await api.get("/admin/leads", { params });
      setLeads(data);
    } catch (err) { nav("/admin/login"); }
  };

  useEffect(() => { if (!localStorage.getItem("hc_admin_token")) nav("/admin/login"); else fetchLeads(); }, [filterStatus, filterType]);

  const openLead = (lead) => {
    setSelected(lead);
    setEdit({ status: lead.status || "new", notes: lead.notes || "", assigned_counsellor: lead.assigned_counsellor || "" });
  };

  const save = async () => {
    try {
      await api.patch(`/admin/leads/${selected.id}`, edit);
      toast.success("Updated");
      setSelected(null);
      fetchLeads();
    } catch { toast.error("Update failed"); }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster richColors position="top-center" />
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-3.5 flex items-center justify-between">
          <Link to="/admin/dashboard" className="inline-flex items-center gap-1.5 text-[#0369A1] font-medium text-sm"><ArrowLeft size={16} /> Dashboard</Link>
          <div className="font-serif font-semibold text-slate-900">Leads CRM</div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="bg-white border border-slate-200 rounded-2xl p-4 flex flex-wrap gap-3 items-center mb-5">
          <Filter size={16} className="text-slate-500" />
          <select data-testid="filter-status" className="input-hc max-w-[200px] text-sm py-2" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
          <select data-testid="filter-type" className="input-hc max-w-[200px] text-sm py-2" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="">All types</option>
            <option value="eligibility">Eligibility</option>
            <option value="counsellor">Counsellor request</option>
            <option value="appointment">Appointment (Dr. Sanjay)</option>
          </select>
          <div className="text-slate-600 text-sm ml-auto">{leads.length} lead{leads.length !== 1 && "s"}</div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <table className="w-full text-[14px]">
            <thead className="bg-slate-50 text-slate-600 text-[12px] uppercase tracking-wider">
              <tr>
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3 hidden md:table-cell">Contact</th>
                <th className="text-left p-3 hidden lg:table-cell">City</th>
                <th className="text-left p-3 hidden md:table-cell">Type</th>
                <th className="text-left p-3 hidden lg:table-cell">Score</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3 hidden lg:table-cell">Created</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => openLead(l)}>
                  <td className="p-3 font-medium text-slate-900">{l.full_name || l.name || "—"}</td>
                  <td className="p-3 hidden md:table-cell">{l.mobile}{l.email && <div className="text-slate-500 text-xs">{l.email}</div>}</td>
                  <td className="p-3 hidden lg:table-cell">{l.city}</td>
                  <td className="p-3 hidden md:table-cell"><span className="chip bg-[#F0F9FF] text-[#0369A1]">{l.lead_type || "—"}</span>{l.patient_type && <span className="ml-1 chip bg-slate-100 text-slate-700">{l.patient_type}</span>}</td>
                  <td className="p-3 hidden lg:table-cell">{l.eligibility_score ?? "—"}</td>
                  <td className="p-3"><span className="chip bg-amber-50 text-amber-800">{l.status}</span></td>
                  <td className="p-3 hidden lg:table-cell text-slate-500">{l.created_at?.slice(0, 10)}</td>
                </tr>
              ))}
              {leads.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-500">No leads found.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <div data-testid="lead-modal" className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setSelected(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-2xl w-full p-6 md:p-8 my-8">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-2xl font-semibold text-slate-900">{selected.full_name || selected.name}</h2>
              <button data-testid="lead-modal-close" onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-900 text-2xl">×</button>
            </div>
            <div className="mt-4 grid sm:grid-cols-2 gap-3 text-[14.5px] text-slate-700">
              <div className="flex items-center gap-2"><Phone size={15} /> {selected.mobile}</div>
              {selected.email && <div className="flex items-center gap-2"><Mail size={15} /> {selected.email}</div>}
              <div className="flex items-center gap-2"><MapPin size={15} /> {selected.city}{selected.state && `, ${selected.state}`}</div>
              {selected.patient_type && <div>Patient: <strong>{selected.patient_type}</strong>{selected.age && ` · age ${selected.age}`}</div>}
            </div>

            {selected.eligibility_result && (
              <div className="mt-5 bg-[#F0F9FF] rounded-xl p-4">
                <div className="text-[12px] uppercase tracking-wider font-bold text-[#0369A1]">Eligibility</div>
                <div className="font-serif text-lg font-semibold mt-1">{selected.eligibility_result} · score {selected.eligibility_score}/100</div>
                <p className="mt-1 text-slate-700 text-[14px]">{selected.result_text}</p>
              </div>
            )}

            {selected.main_concern && <div className="mt-4 text-[14.5px] text-slate-700"><strong>Concern:</strong> {selected.main_concern}</div>}
            {selected.current_diagnosis && <div className="mt-2 text-[14.5px] text-slate-700"><strong>Diagnosis:</strong> {selected.current_diagnosis}</div>}
            {selected.message && <div className="mt-2 text-[14.5px] text-slate-700"><strong>Message:</strong> {selected.message}</div>}

            <div className="mt-6 space-y-3 border-t border-slate-100 pt-5">
              <div>
                <label className="label-hc">Status</label>
                <select data-testid="lead-status" className="input-hc" value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                </select>
              </div>
              <div>
                <label className="label-hc">Assigned counsellor</label>
                <input data-testid="lead-counsellor" className="input-hc" value={edit.assigned_counsellor} onChange={(e) => setEdit({ ...edit, assigned_counsellor: e.target.value })} />
              </div>
              <div>
                <label className="label-hc">Notes</label>
                <textarea data-testid="lead-notes" rows={3} className="input-hc" value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} />
              </div>
              <button data-testid="lead-save" onClick={save} className="btn-primary">Save changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
